import { useState, useMemo } from 'react'
import { getPedidos } from '../utils/storage'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { DollarSign, TrendingUp, User } from 'lucide-react'

import { fmt } from '../utils/fmt'
const TAXA = 0.015

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
]

const COR_VENDEDORA = {
  'Daíze':   { bg: '#FFF9C2', cor: '#CC8800', dark: '#111111' },
  'Natália': { bg: '#DCFCE7', cor: '#16A34A', dark: '#111111' },
  'Sócio':   { bg: '#DBEAFE', cor: '#1D4ED8', dark: '#111111' },
  'Outros':  { bg: '#F1F5F9', cor: '#64748B', dark: '#111111' },
}

function fmtDate(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return '—' }
}

export function Comissoes() {
  const hoje = new Date()
  const [mes,  setMes]  = useState(hoje.getMonth())   // 0-based
  const [ano,  setAno]  = useState(hoje.getFullYear())
  const [sel,  setSel]  = useState(null)               // vendedora selecionada

  // Exclui pedidos fantasmas de ajuste (AJ-) e pedidos filhos divididos.
  // Filhos não são vendas (mãe carrega o valor) — não rendem comissão extra.
  const pedidos = useMemo(() => getPedidos().filter(p => !p.tipoAjuste && !p.tipoFilho), [])

  const anos = useMemo(() => {
    const set = new Set(pedidos.map(p => {
      const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
      return d ? new Date(d + 'T12:00:00').getFullYear() : null
    }).filter(Boolean))
    return [...set].sort((a, b) => b - a)
  }, [pedidos])

  const pedidosPeriodo = useMemo(() => {
    return pedidos.filter(p => {
      const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
      if (!d) return false
      const dt = new Date(d + 'T12:00:00')
      return dt.getFullYear() === ano && dt.getMonth() === mes
    })
  }, [pedidos, mes, ano])

  const porVendedora = useMemo(() => {
    const grupos = {}
    pedidosPeriodo.forEach(p => {
      const v = p.vendedora || 'Outros'
      if (!grupos[v]) grupos[v] = []
      grupos[v].push(p)
    })
    return grupos
  }, [pedidosPeriodo])

  const vendedoras = ['Daíze', 'Natália', 'Sócio', 'Outros'].filter(v =>
    porVendedora[v]?.length > 0 ||
    ['Daíze','Natália','Sócio'].includes(v)
  )

  const pedidosSel = sel ? (porVendedora[sel] || []) : pedidosPeriodo

  const totalGeral = pedidosPeriodo.reduce((s, p) => s + (p.valorTotal || 0), 0)
  const comissaoGeral = totalGeral * TAXA

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign size={18} className="text-[#CC8800]" />
            Comissões
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {pedidosPeriodo.length} pedido{pedidosPeriodo.length !== 1 ? 's' : ''} ·
            Total: {fmt(totalGeral)} · Comissão: {fmt(comissaoGeral)}
          </p>
        </div>

        {/* Seletor de período */}
        <div className="flex items-center gap-2">
          <select
            value={mes}
            onChange={e => setMes(Number(e.target.value))}
            className="input text-sm py-1.5 px-3 w-36"
          >
            {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select
            value={ano}
            onChange={e => setAno(Number(e.target.value))}
            className="input text-sm py-1.5 px-3 w-24"
          >
            {(anos.length ? anos : [hoje.getFullYear()]).map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {sel && (
            <button
              onClick={() => setSel(null)}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors px-2 py-1.5 rounded-lg border border-slate-200 hover:border-red-200"
            >
              Limpar filtro
            </button>
          )}
        </div>
      </div>

      {/* Cards por vendedora */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {vendedoras.map(v => {
          const lista    = porVendedora[v] || []
          const total    = lista.reduce((s, p) => s + (p.valorTotal || 0), 0)
          const comissao = total * TAXA
          const cores    = COR_VENDEDORA[v] || COR_VENDEDORA['Outros']
          const ativo    = sel === v
          return (
            <button
              key={v}
              type="button"
              onClick={() => setSel(ativo ? null : v)}
              className={`text-left p-4 rounded-2xl border-2 transition-all
                ${ativo
                  ? 'bg-[#111111] border-[#111111] text-white shadow-lg scale-105'
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${ativo ? 'bg-white/20 text-white' : ''}`}
                  style={!ativo ? { backgroundColor: cores.bg, color: cores.cor } : {}}
                >
                  {v.charAt(0)}
                </span>
                <span className={`text-sm font-bold ${ativo ? 'text-white' : 'text-slate-800'}`}>{v}</span>
              </div>
              <p className={`text-[10px] uppercase tracking-wide mb-0.5 ${ativo ? 'text-white/50' : 'text-slate-400'}`}>
                {lista.length} pedido{lista.length !== 1 ? 's' : ''}
              </p>
              <p className={`text-base font-black ${ativo ? 'text-[#F5C200]' : 'text-slate-700'}`}>
                {fmt(comissao)}
              </p>
              <p className={`text-[10px] mt-0.5 ${ativo ? 'text-white/40' : 'text-slate-400'}`}>
                {fmt(total)} em vendas
              </p>
            </button>
          )
        })}
      </div>

      {/* Tabela de pedidos */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-700">
            {sel ? `Pedidos — ${sel}` : 'Todos os pedidos do período'}
          </p>
          <div className="text-xs text-slate-400">
            {pedidosSel.length} pedido{pedidosSel.length !== 1 ? 's' : ''} ·
            comissão total: <strong className="text-[#CC8800]">{fmt(pedidosSel.reduce((s, p) => s + (p.valorTotal || 0), 0) * TAXA)}</strong>
          </div>
        </div>

        {pedidosSel.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-10">Nenhum pedido neste período</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="text-left px-4 py-2.5">Nº</th>
                  <th className="text-left px-4 py-2.5">Data</th>
                  <th className="text-left px-4 py-2.5">Cliente</th>
                  <th className="text-left px-4 py-2.5">Vendedora</th>
                  <th className="text-right px-4 py-2.5">Valor</th>
                  <th className="text-right px-4 py-2.5">Comissão 1,5%</th>
                </tr>
              </thead>
              <tbody>
                {[...pedidosSel]
                  .sort((a, b) => (b.dataVenda || '').localeCompare(a.dataVenda || ''))
                  .map((p, i) => {
                    const comissao = (p.valorTotal || 0) * TAXA
                    const v = p.vendedora || 'Outros'
                    const cores = COR_VENDEDORA[v] || COR_VENDEDORA['Outros']
                    return (
                      <tr key={p.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                        <td className="px-4 py-2.5 font-mono text-slate-400">{p.numero}</td>
                        <td className="px-4 py-2.5 text-slate-600">{fmtDate(p.dataVenda)}</td>
                        <td className="px-4 py-2.5 font-semibold text-slate-800">{p.cliente?.nome || '—'}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                            style={{ backgroundColor: cores.bg, color: cores.cor }}
                          >
                            {v}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-700">{fmt(p.valorTotal)}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-[#CC8800]">{fmt(comissao)}</td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-600">Total</td>
                  <td className="px-4 py-3 text-right text-sm font-black text-slate-800">
                    {fmt(pedidosSel.reduce((s, p) => s + (p.valorTotal || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-black text-[#CC8800]">
                    {fmt(pedidosSel.reduce((s, p) => s + (p.valorTotal || 0), 0) * TAXA)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
